/**
 * useEmailSendHistory - Phase 10a.3 Email Delivery Tracking
 *
 * React Query hook for fetching email send history per entity.
 * Returns all sends (not just latest) with delivery status from Mailgun webhooks.
 */
import { useQuery } from '@tanstack/react-query';
import { api } from '@/lib/api';

export interface EmailSend {
  id: string;
  tenant_id: string;
  entity_type: 'invoice' | 'proposal';
  entity_id: string;
  mailgun_message_id: string | null;
  /** Legacy single-recipient field — kept via trigger, to_list is source of truth post-V155 */
  recipient: string;
  /** R6: Multi-TO list (post-V155); pre-V155 rows backfilled via trigger to [recipient] */
  to_list: string[] | null;
  /** R6: CC list (post-V155); null for pre-V155 rows */
  cc_list: string[] | null;
  bcc_list: string[] | null;
  reply_to: string | null;
  subject: string | null;
  sent_by: string;
  sent_at: string;
  custom_note: string | null;
  delivery_status: 'pending' | 'delivered' | 'bounced' | 'failed';
  delivery_updated_at: string | null;
  created_at: string;
}

interface EmailSendHistoryResponse {
  items: EmailSend[];
  total: number;
  limit: number;
  offset: number;
}

export function useEmailSendHistory(
  entityType: 'invoice' | 'proposal',
  entityId: string | undefined,
  options?: { limit?: number; offset?: number; enabled?: boolean }
) {
  const { limit = 50, offset = 0, enabled = true } = options ?? {};

  return useQuery<EmailSendHistoryResponse>({
    queryKey: ['email-send-history', entityType, entityId, { limit, offset }],
    queryFn: async () => {
      const params = new URLSearchParams({
        entity_type: entityType,
        entity_id: entityId!,
        limit: String(limit),
        offset: String(offset),
      });
      const response = await api.get(`/api/email-sends?${params}`);
      return response as EmailSendHistoryResponse;
    },
    staleTime: 15000, // 15s cache — delivery status may update via webhooks
    enabled: enabled && !!entityId,
  });
}
